/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { C, t, fmt$, formatDate } from "../shared/globals.js";

function TradesGrid({ entries, currentPrice }) {
  const [hovered, setHovered] = useState(null);
  const wrapRef = useRef(null);
  const [containerW, setContainerW] = useState(600);

  useEffect(() => {
    const obs = new ResizeObserver(es => setContainerW(es[0].contentRect.width || 600));
    if (wrapRef.current) {
      obs.observe(wrapRef.current);
      requestAnimationFrame(() => {
        if (wrapRef.current) {
          const { width } = wrapRef.current.getBoundingClientRect();
          if (width > 0) setContainerW(width);
        }
      });
    }
    return () => obs.disconnect();
  }, []);

  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));

  const DOT = 16;
  const GAP = 2;
  const COLS = Math.floor((containerW + GAP) / (DOT + GAP));
  const ROWS = Math.ceil(sorted.length / Math.max(COLS, 1));
  const W = containerW;
  const H = ROWS * (DOT + GAP) - GAP;
  // Recalculate actual dot size to fill width perfectly
  const actualDot = COLS > 0 ? Math.floor((containerW - (COLS - 1) * GAP) / COLS) : DOT;

  const fmt$ = v => new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", minimumFractionDigits:2 }).format(v);
  const fmtDate = s => { const [y,m,d]=s.split("-"); return `${d}.${m}.${y.slice(2)}`; };

  const profitCount = sorted.filter(e => e.shares > 0 && (e.dollars/e.shares) <= currentPrice).length;
  const lossCount   = sorted.length - profitCount;

  return (
    <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10,
      padding:"16px 20px" }}>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
          letterSpacing:"0.3em", color:"var(--text4)" }}>
          TRADES · {sorted.length} TOTAL
        </div>
        <div style={{ display:"flex", gap:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:10, height:10, borderRadius:"50%", background:C.green }}/>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
              color:C.green, letterSpacing:"0.1em" }}>{profitCount} {t("tradesInProfit")}</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:10, height:10, borderRadius:"50%", background:C.red }}/>
            <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
              color:C.red, letterSpacing:"0.1em" }}>{lossCount} {t("tradesUnderwater")}</span>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div ref={wrapRef} style={{ position:"relative", width:"100%", overflow:"hidden" }}>
        <svg width="100%" height={Math.max(H, 1)} viewBox={`0 0 ${W} ${Math.max(H, 1)}`}
          style={{ display:"block", overflow:"hidden" }}>
          {sorted.map((e, i) => {
            const col      = i % COLS;
            const row      = Math.floor(i / COLS);
            const cx       = col * (actualDot + GAP) + actualDot / 2;
            const cy       = row * (actualDot + GAP) + actualDot / 2;
            const buyPrice = e.shares > 0 ? e.dollars / e.shares : 0;
            const inProfit = buyPrice <= currentPrice;
            const isHov    = hovered === i;
            const baseColor = inProfit ? C.green : C.red;
            const r = (actualDot / 2) * 0.8;

            const dotR = isHov ? r * 1.2 : r;

            const dotOpacity = 0.6;

            return (
              <circle key={e.id || i}
                cx={cx} cy={cy} r={dotR}
                fill={baseColor}
                opacity={isHov ? 1 : dotOpacity}
                stroke={isHov ? baseColor : "transparent"}
                strokeWidth="2"
                style={{ cursor:"default", transition:"r 0.2s ease" }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              />
            );
          })}
        </svg>

        {/* Tooltip */}
        {hovered !== null && sorted[hovered] && (() => {
          const e = sorted[hovered];
          const col = hovered % COLS;
          const row = Math.floor(hovered / COLS);
          const cx  = col * (actualDot + GAP) + actualDot / 2;
          const cy  = row * (actualDot + GAP) + actualDot / 2;
          const buyPrice = e.shares > 0 ? e.dollars / e.shares : 0;
          const inProfit = buyPrice <= currentPrice;
          const pnl = (currentPrice - buyPrice) * e.shares;
          const pnlPct = buyPrice > 0 ? ((currentPrice - buyPrice) / buyPrice) * 100 : 0;
          const tipX = cx + (col > COLS - 6 ? -165 : 14);
          const tipY = cy + (row > ROWS - 4 ? -100 : 0);
          return (
            <div style={{
              position:"absolute",
              left: tipX, top: tipY,
              background:"var(--bg2)",
              border:`1px solid ${inProfit ? C.green : C.red}`,
              borderRadius:8, padding:"10px 14px",
              fontFamily:"Montserrat,sans-serif",
              pointerEvents:"none", zIndex:20, minWidth:155,
            }}>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em",
                color:"var(--text4)", marginBottom:7 }}>
                #{hovered + 1} · {fmtDate(e.date)}
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                <div style={{ display:"flex", justifyContent:"space-between", gap:12 }}>
                  <span style={{ fontSize:9, color:"var(--text4)" }}>BUY PRICE</span>
                  <span style={{ fontSize:11, fontWeight:700, color:"var(--text)" }}>{fmt$(buyPrice)}</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", gap:12 }}>
                  <span style={{ fontSize:9, color:"var(--text4)" }}>INVESTED</span>
                  <span style={{ fontSize:11, fontWeight:700, color:"var(--text)" }}>{fmt$(e.dollars)}</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", gap:12 }}>
                  <span style={{ fontSize:9, color:"var(--text4)" }}>SHARES</span>
                  <span style={{ fontSize:11, fontWeight:700, color:"var(--text)" }}>{e.shares.toFixed(4)}</span>
                </div>
                <div style={{ height:1, background:"var(--border)", margin:"3px 0" }}/>
                <div style={{ display:"flex", justifyContent:"space-between", gap:12 }}>
                  <span style={{ fontSize:9, color:"var(--text4)" }}>P&L</span>
                  <span style={{ fontSize:12, fontWeight:900,
                    color: inProfit ? C.green : C.red }}>
                    {pnl >= 0 ? "+" : ""}{fmt$(pnl)} ({pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}


export default TradesGrid;
